import {
  SmartSearchBotResponse,
  ToolCallParameters,
} from '@ltvco/refresh-lib/api';
import { fetchReportByPermalinkInLine } from '@ltvco/refresh-lib/v2';
import OpenAI from 'openai';

import {
  FUNCTION_CALL_OUTPUT,
  FUNCTION_CALL_TYPE,
  GENERIC_ERROR_MESSAGE,
  MESSAGE,
  NOT_SUPPORTED_SEARCH_TYPE,
  OUTPUT_TEXT,
  REPORT_PROCESSORS,
  SEARCH_TYPES,
  TRIGGER_SEARCH,
  UNSUPPORTED_SEARCH_ERROR_MESSAGE,
  UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
  CUSTOMER_SERVICE_CONTACT,
  ROLES,
  USE_OPENAI_LOCAL_CONFIGURATION,
} from 'components/ChatBV/constants';
import {
  GenericLLMResponse,
  ProcessedFunctionCallResponse,
  PersonParamsFunctionArguments,
  PersonTeaserCondensedResult,
  GenericReportResponse,
  FunctionCallResponse,
  PermalinkOrTeaserOrParamsResult,
  BaseMessage,
  GenericLLMRequest,
  LLMRequestItem,
  InputItem,
  LLMFunctionCallOutput,
  ReponsesAPIRequestProps,
} from 'components/ChatBV/types';
import {
  getPhoneNumberPermalink,
  getEmailPermalink,
  getPropertyPermalink,
  getVehiclePermalink,
  getPublicRecordsTeaser,
} from 'components/ChatBV/utils';

export const mapLLMServiceResponseToGenericLLMResponse = (
  response: SmartSearchBotResponse
): GenericLLMResponse => {
  if (
    Array.isArray(response.data?.tool_calls) &&
    response.data.tool_calls.length > 0
  ) {
    const toolCalls = response.data.tool_calls;
    const functionCalls: FunctionCallResponse[] = toolCalls.map((toolCall) => ({
      call_id: toolCall.call_id,
      name: toolCall.tool,
      arguments: toolCall.parameters,
      type: 'function_call',
      status: 'completed',
    }));

    return {
      context_id: response.context_id,
      data: [null, functionCalls],
      response_id: response.response_id,
    };
  }

  return {
    context_id: response.context_id,
    data: [response.data?.answer ?? '', null],
    response_id: response.response_id,
  };
};

const determineSearchType = (args: ToolCallParameters) => {
  if (args.search_type) {
    return args.search_type;
  }
  if (args.phone_number) {
    return SEARCH_TYPES.PHONE;
  }
  if (args.email) {
    return SEARCH_TYPES.EMAIL;
  }
  if (args.address && args.city && args.state && args.zipcode) {
    return SEARCH_TYPES.PROPERTY;
  }
  if (
    (args.vehicle_year &&
      args.vehicle_make &&
      args.vehicle_model &&
      args.vehicle_trim) ||
    args.vehicle_vin ||
    (args.vehicle_license_plate && args.state)
  ) {
    return SEARCH_TYPES.VEHICLE;
  }
  if (args.first_name && args.last_name) {
    return SEARCH_TYPES.PERSON;
  }
  return NOT_SUPPORTED_SEARCH_TYPE;
};

export const handleFunctionCalls = async (
  functionCalls: FunctionCallResponse[]
): Promise<ProcessedFunctionCallResponse[]> => {
  const processedFunctionCalls: ProcessedFunctionCallResponse[] = [];

  for (const functionCall of functionCalls) {
    if (functionCall.arguments && functionCall.name) {
      if (functionCall.name === TRIGGER_SEARCH) {
        const search_type = determineSearchType(functionCall.arguments);
        const callId = functionCall.call_id;

        if (search_type === NOT_SUPPORTED_SEARCH_TYPE) {
          processedFunctionCalls.push(
            createFunctionCallResultForTriggerSearch(
              functionCall.call_id,
              UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
              null,
              search_type,
              null
            )
          );
        } else {
          try {
            const searchHandlers = {
              [SEARCH_TYPES.PHONE]: async () => {
                const { phone_number } = functionCall.arguments!;
                if (phone_number) {
                  const sanitizedPhoneNumber = phone_number.replace(/\D/g, '');
                  if (sanitizedPhoneNumber.length !== 10) {
                    return {
                      error: 'Phone number must be 10 digits',
                    };
                  }
                  return await getPhoneNumberPermalink(sanitizedPhoneNumber);
                }
                return {
                  error: 'Phone number is required',
                };
              },
              [SEARCH_TYPES.EMAIL]: async () => {
                const { email } = functionCall.arguments!;
                if (email) {
                  return await getEmailPermalink(email);
                }
                return {
                  error: 'Email is required',
                };
              },
              [SEARCH_TYPES.PROPERTY]: async () => {
                const { address, city, state, zipcode } =
                  functionCall.arguments!;
                if (
                  !address ||
                  !city ||
                  !state ||
                  !zipcode ||
                  isUnknown(address) ||
                  isUnknown(city) ||
                  isUnknown(state) ||
                  isUnknown(zipcode)
                ) {
                  const missingParams = [];
                  if (!address || isUnknown(address)) {
                    missingParams.push('street address');
                  }
                  if (!city || isUnknown(city)) {
                    missingParams.push('city');
                  }
                  if (!state || isUnknown(state)) {
                    missingParams.push('state');
                  }
                  if (!zipcode || isUnknown(zipcode)) {
                    missingParams.push('zipcode');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                return await getPropertyPermalink(
                  address,
                  city,
                  state,
                  zipcode
                );
              },
              [SEARCH_TYPES.VEHICLE]: async () => {
                const {
                  vehicle_year,
                  vehicle_make,
                  vehicle_model,
                  vehicle_trim,
                  vehicle_vin,
                  vehicle_license_plate,
                  state,
                } = functionCall.arguments!;
                return await getVehiclePermalink({
                  state,
                  year: vehicle_year,
                  make: vehicle_make,
                  model: vehicle_model,
                  trim: vehicle_trim,
                  vin: vehicle_vin,
                  licensePlate: vehicle_license_plate,
                });
              },
              [SEARCH_TYPES.PERSON]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                  title,
                  company,
                  industry,
                  level,
                  birth_month,
                  birth_year,
                  death_month,
                  death_year,
                } = functionCall.arguments!;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  const missingParams = [];
                  if (!first_name || isUnknown(first_name)) {
                    missingParams.push('first name');
                  }
                  if (!last_name || isUnknown(last_name)) {
                    missingParams.push('last name');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                const teaserResult = await getPublicRecordsTeaser({
                  first_name,
                  last_name,
                  middle_name: middle_name ?? '',
                  age: age ?? '',
                  state: state ?? '',
                  city: city ?? '',
                });
                return {
                  teaser: teaserResult,
                  params: {
                    first_name,
                    last_name,
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: title ?? '',
                    company: company ?? '',
                    industry: industry ?? '',
                    level: level ?? '',
                    birth_month: birth_month ?? '',
                    birth_year: birth_year ?? '',
                    death_month: death_month ?? '',
                    death_year: death_year ?? '',
                  },
                };
              },
            };

            const handler = searchHandlers[search_type];
            const permalinkOrTeaserOrParamsResult: PermalinkOrTeaserOrParamsResult =
              handler ? await handler() : { permalink: undefined };
            if (permalinkOrTeaserOrParamsResult.permalink) {
              const { data, rawData } = await fetchReportByPermalink(
                permalinkOrTeaserOrParamsResult.permalink || '',
                search_type
              );
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  data,
                  permalinkOrTeaserOrParamsResult?.permalink,
                  search_type,
                  rawData as GenericReportResponse
                )
              );
            } else if (permalinkOrTeaserOrParamsResult.teaser) {
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  {
                    amountOfResults:
                      permalinkOrTeaserOrParamsResult.teaser.amountOfResults,
                    city: permalinkOrTeaserOrParamsResult.params?.city || '',
                    state: permalinkOrTeaserOrParamsResult.params?.state || '',
                  },
                  null,
                  search_type,
                  null,
                  permalinkOrTeaserOrParamsResult.teaser.teaserData,
                  permalinkOrTeaserOrParamsResult.params
                    ? permalinkOrTeaserOrParamsResult.params
                    : null
                )
              );
            } else {
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  permalinkOrTeaserOrParamsResult.error,
                  null,
                  search_type,
                  null
                )
              );
            }
          } catch (error) {
            processedFunctionCalls.push(
              createFunctionCallResultForTriggerSearch(
                callId,
                error instanceof Error ? error.message : JSON.stringify(error),
                null,
                search_type,
                null
              )
            );
          }
        }
      }

      if (functionCall.name === CUSTOMER_SERVICE_CONTACT) {
        processedFunctionCalls.push(
          createFunctionCallResultForCustomerServiceContact(
            functionCall.call_id
          )
        );
      }
    }
  }

  return processedFunctionCalls;
};

const createFunctionCallResultForTriggerSearch = (
  functionCallId: string,
  output: any,
  permalink: string | null,
  searchType: string | null,
  rawData: GenericReportResponse | null,
  teaser: PersonTeaserCondensedResult[] | null = null,
  params: PersonParamsFunctionArguments | null = null
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output: JSON.stringify(output),
  permalink: permalink || undefined,
  searchType: searchType || undefined,
  reportData: rawData || undefined,
  teaser: teaser || undefined,
  params: params || undefined,
});

const createFunctionCallResultForCustomerServiceContact = (
  functionCallId: string
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output:
    'Repeat this specific message: "Please refer to the customer service contact information provided."',
});

const isUnknown = (value: string) => value === 'Unknown';

export const fetchReportByPermalink = async (
  permalink: string,
  searchType: string
): Promise<{ data: any; rawData: GenericReportResponse | null }> => {
  try {
    const coreResult = await fetchReportByPermalinkInLine(
      permalink,
      searchType
    );
    let processedResult: any = coreResult;

    const reportProcessor =
      REPORT_PROCESSORS[searchType as keyof typeof REPORT_PROCESSORS];

    const processedReportResult = reportProcessor
      ? reportProcessor(coreResult as any)
      : null;
    if (processedReportResult) {
      processedResult = processedReportResult;
    }
    return { data: processedResult, rawData: coreResult };
  } catch (error) {
    if (error instanceof Error) {
      return { data: error.message, rawData: null };
    }
    return { data: GENERIC_ERROR_MESSAGE, rawData: null };
  }
};

const parseFunctionCallArguments = (
  functionCallArgs: string
): ToolCallParameters | null => {
  try {
    return JSON.parse(functionCallArgs || '{}') as ToolCallParameters;
  } catch (_error) {
    return null;
  }
};

export const mapResponsesAPIResponseToProcessedResponse = (
  response: OpenAI.Responses.Response
): GenericLLMResponse => {
  if (response && response.output && response.output.length > 0) {
    const functionCalls = response.output.filter(
      (output) => output.type === FUNCTION_CALL_TYPE
    );

    if (functionCalls.length > 0) {
      const functionCallsData: FunctionCallResponse[] = functionCalls.map(
        (functionCall) => {
          const functionArgs = parseFunctionCallArguments(
            functionCall.arguments
          );
          return {
            name: functionCall.name ?? '',
            arguments: functionArgs ?? {},
            call_id: functionCall.call_id,
            type: functionCall.type ?? '',
            status: functionCall.status ?? '',
          };
        }
      );
      return {
        context_id: '',
        data: [null, functionCallsData],
        response_id: response.id,
      };
    }

    const genericLLMResponse: GenericLLMResponse = {
      context_id: '',
      data: [extractOutputText(response.output), null],
      response_id: response.id,
    };

    return genericLLMResponse;
  }

  const genericLLMResponse: GenericLLMResponse = {
    context_id: '',
    data: [UNSUPPORTED_SEARCH_ERROR_MESSAGE, null],
    response_id: response.id,
  };
  return genericLLMResponse;
};

export const extractOutputText = (
  output: any[],
  fallbackMessage: string = GENERIC_ERROR_MESSAGE
): string => {
  if (!output || output.length === 0) {
    return fallbackMessage;
  }

  const firstOutput = output[0];
  if (
    firstOutput.type === MESSAGE &&
    firstOutput.content &&
    firstOutput.content.length > 0 &&
    firstOutput.content[0]?.type === OUTPUT_TEXT
  ) {
    return firstOutput.content[0].text || fallbackMessage;
  }

  return fallbackMessage;
};

export const buildUserRequest = (
  message: BaseMessage,
  lastContextId?: string
): GenericLLMRequest => ({
  items: [
    {
      type: null,
      call_id: null,
      output: null,
      role: ROLES.USER,
      content: message.content,
    },
  ],
  context_id: lastContextId || null,
});

export const buildLLMOrientedRequest = (
  request: GenericLLMRequest
): ReponsesAPIRequestProps | LLMRequestItem => {
  if (USE_OPENAI_LOCAL_CONFIGURATION) {
    const itemsToSend: InputItem[] = [];

    for (const item of request.items) {
      if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
        itemsToSend.push({
          type: item.type,
          call_id: item.call_id,
          output: item.output,
        });
      } else {
        itemsToSend.push({
          role: ROLES.USER,
          content: item.content || '',
        });
      }
    }
    return {
      input: itemsToSend,
      previous_response_id: request.context_id || undefined,
    };
  }
  // If not using local configuration, we need to build the request for the LLM

  const functionCallOutputs: LLMFunctionCallOutput[] = [];
  const prompt: string[] = [];

  for (const item of request.items) {
    if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
      functionCallOutputs.push({
        id: item.call_id,
        content: item.output,
      });
    } else {
      prompt.push(item.content || '');
    }
  }

  const requestToSend: LLMRequestItem = {
    ...(prompt.length > 0 && { prompt: prompt.toString() }),
    ...(functionCallOutputs.length > 0 && {
      function_call_outputs: functionCallOutputs,
    }),
    contextId: request.context_id || undefined,
  };

  return requestToSend;
};
